

import React, { useState, useMemo, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { Grid, Pagination } from "@mui/material";
import { useAuth } from "../../../../../features/auth/AuthContext";
import api from "../../../../../api/api";
import ActivityLogFilter from "./ActivityLogFilter";
import { ReactSVG } from "react-svg";
import imgs from "../../../../../assets/images/imgs";
import { FilterSVG } from "../../../../../assets/images/images";

// ---- Badge styling per action type (matches `action_display` from the API) ----
const ACTION_STYLES = {
  Retrieved: "border-sky-200 bg-sky-50 text-sky-600",
  Created: "border-emerald-200 bg-emerald-50 text-emerald-600",
  Updated: "border-amber-200 bg-amber-50 text-amber-600",
  Deleted: "border-rose-200 bg-rose-50 text-rose-600",
  "Logged In": "border-sky-200 bg-sky-50 text-sky-600",
  "Logged Out": "border-slate-200 bg-slate-50 text-slate-500",
};

function ActionBadge({ action }) {
  const style = ACTION_STYLES[action] || "border-slate-200 bg-slate-50 text-slate-500";
  return (
    <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium ${style}`}>
      {action || "Unknown"}
    </span>
  );
}

function formatDateTime(isoString) {
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return { date: "-", time: "-" };
  const date = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
  return { date, time };
}

// Simple filter icon for the trigger button
function FilterIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />
    </svg>
  );
}

const EMPTY_FILTERS = { actions: [], start_date: null, end_date: null };

export default function ActivityLog() {
  const { token } = useAuth();
  const [query, setQuery] = useState("");
  const [sortDir, setSortDir] = useState("desc"); // "asc" | "desc"
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  // ---- filter drawer state ----
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  // ---- pagination state ----
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(null);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const pageSizeRef = useRef(0);

  const globalstate = useSelector((state) => state?.globalState);
  const [debouncedQuery, setDebouncedQuery] = useState("");

  // Debounce: wait 450ms after the user stops typing before hitting the API
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQuery(query.trim());
      setPage(1); // new search → start from page 1
    }, 450);
    return () => clearTimeout(t);
  }, [query]);

  const fetchActivityLog = async () => {
    if (!token) return; // wait for auth context to hydrate the token
    setLoading(true);
    try {
      // Build query params. Server-side filtering by action + date range.
      const params = { page };
      if (debouncedQuery) params.search = debouncedQuery;          // ?search=notifications
      if (filters.actions.length > 0) {
        // Sent as a comma-separated list: ?action=CREATED,UPDATED
        // If your DRF view expects repeated params (?action=CREATED&action=UPDATED),
        // pass an array here and add a paramsSerializer (see note below).
        params.action = filters.actions.join(",");
      }
      if (filters.start_date) params.date_from = filters.start_date; // YYYY-MM-DD
      if (filters.end_date) params.date_to = filters.end_date;       // YYYY-MM-DD

      const response = await api.get("/activities/api/access-logs/", {
        params,
        // For repeated params instead of comma-joined, use:
        // paramsSerializer: (p) => new URLSearchParams(
        //   Object.entries(p).flatMap(([k, v]) =>
        //     Array.isArray(v) ? v.map((x) => [k, x]) : [[k, v]]
        //   )
        // ).toString(),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Token ${token}`,
        },
      });

      const responseData = response?.data;
      const isPaginated = !Array.isArray(responseData);
      const logs = Array.isArray(responseData)
        ? responseData
        : responseData?.results || responseData?.data || [];

      setData(logs);

      if (isPaginated) {
        const count = responseData?.count ?? logs.length;
        setTotalCount(count);
        setHasNext(Boolean(responseData?.next));
        setHasPrev(Boolean(responseData?.previous));

        // measure server page size from any page that still has a `next`
        if (responseData?.next && logs.length > pageSizeRef.current) {
          pageSizeRef.current = logs.length;
        }
        const size = pageSizeRef.current || logs.length || 1;
        setTotalPages(Math.max(1, Math.ceil(count / size)));
      } else {
        setTotalCount(logs.length);
        setHasNext(false);
        setHasPrev(false);
        setTotalPages(1);
      }
    } catch (error) {
      if (error.response) {
        console.error("API error:", error.response.status, error.response.data);
      } else if (error.request) {
        console.error("No response received:", error.request);
      } else {
        console.error("Request setup error:", error.message);
      }
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  // Refetch on auth, page, or applied filters change.
  useEffect(() => {
    fetchActivityLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, page, filters, debouncedQuery]);

  const handlePageChange = (event, value) => {
    window.scrollTo(0, 0);
    setPage(value);
  };

  // Called by the drawer's Apply / Reset. Reset page to 1 so results start fresh.
  const handleApplyFilters = (payload) => {
    setFilters(payload || EMPTY_FILTERS);
    setPage(1);
  };

  const activeFilterCount =
    filters.actions.length + (filters.start_date || filters.end_date ? 1 : 0);

  // NOTE: search + sort run on the current page (action/date filtering is server-side).
  const filtered = useMemo(() => {
    let rows = [...data];

    if (query.trim()) {
      const q = query.trim().toLowerCase();
      rows = rows.filter(
        (r) =>
          r.message?.toLowerCase().includes(q) ||
          r.action_display?.toLowerCase().includes(q) ||
          r.endpoint?.toLowerCase().includes(q) ||
          r.method?.toLowerCase().includes(q)
      );
    }

    rows.sort((a, b) => {
      const diff = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      return sortDir === "asc" ? diff : -diff;
    });

    return rows;
  }, [data, query, sortDir]);


  function RefreshIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 11a8.1 8.1 0 0 0-15.5-2M4 5v4h4" />
      <path d="M4 13a8.1 8.1 0 0 0 15.5 2M20 19v-4h-4" />
    </svg>
  );
}

  return (
    <div className="min-h-full w-full p-6 sm:p-10">
      <div className="mx-auto rounded-2xl border border-slate-100 bg-white p-6 shadow-sm sm:p-8">
        {/* Header */}
        <div className=" flex flex-col ">
          <h2 className="m-0 text-lg font-semibold text-slate-900">
            Activity Log ({totalCount})
          </h2>
<div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center">

  {/* Search */}
  <div
    className="studentDashboar-search-filter StudentCourseCard-lessons-search-filterb w-full sm:flex-1"
    style={{ position: "relative" }}
  >
    <input
      type="text"
      value={query}
      onChange={(e) => setQuery(e.target.value)}
      placeholder="Search activity..."
      className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-10 pr-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-100"
    />

    <div className="serach-input-icon">
      <ReactSVG src={imgs.svg.search} alt="" />
    </div>
  </div>

  {/* Filter + Refresh */}
  <div className="flex w-full shrink-0 items-center gap-2 sm:w-auto">

    {/* Filter Button */}
    <span
      type="button"
      onClick={() => setFilterOpen(true)}
      className={`inline-flex flex-1 items-center justify-center gap-2 px-3.5 py-2 text-sm font-medium transition-colors sm:flex-none ${
        activeFilterCount > 0
          ? "border-blue-200 bg-blue-100 text-blue-600"
          : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
      }`}
    >
      <img src={FilterSVG} alt="filter" />

      Filter

      {activeFilterCount > 0 && (
        <span className="ml-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-white text-[10px] font-semibold text-blue-600">
          {activeFilterCount}
        </span>
      )}
    </span>

    {/* Refresh Button */}
    <button
      type="button"
      onClick={fetchActivityLog}
      disabled={loading}
      className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 sm:flex-none"
      title="Refresh activity logs"
    >
      <RefreshIcon />
      <span>Refresh</span>
    </button>

  </div>
</div>
        </div>



        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse">
            <thead>
              <tr className="border-b border-slate-100 text-left text-xs font-medium uppercase tracking-wide text-slate-400">
                <th
                  onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                  className="pb-3 pr-4 font-medium flex items-center gap-1.5 normal-case uppercase tracking-wide text-slate-400 hover:text-slate-600 cursor-pointer"
                >
                  Date {sortDir === "asc" ? "↑" : "↓"}
                </th>
                <th className="pb-3 pr-4 font-medium">Action</th>
                <th className="pb-3 pr-4 font-medium">Details</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="py-10 text-center text-sm text-slate-400">
                    Loading activity...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-10 text-center text-sm text-slate-400">
                    No activity matches your search or filters.
                  </td>
                </tr>
              ) : (
                filtered.map((row) => {
                  const { date, time } = formatDateTime(row.created_at);
                  return (
                    <tr key={row.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60">
                      <td className="whitespace-nowrap py-4 pr-4 align-top text-sm text-slate-600">
                        {date}
                        <br />
                        <span className="text-slate-400">{time}</span>
                      </td>
                      <td className="py-4 pr-4 align-top">
                        <ActionBadge action={row.action_display} />
                      </td>
                      <td className="py-4 pr-4 align-top text-sm text-slate-600">
                        {row.message}
                        {row.hits_count > 1 && (
                          <span className="ml-2 text-xs text-slate-400">×{row.hits_count}</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && filtered.length != 0 && (
          <div className="mt-4 flex flex-col items-center gap-2 sm:flex-row sm:justify-between">
            {/* <span className="text-xs text-slate-400">
              {totalCount !== null ? `${totalCount} total records` : ""}
            </span> */}
            <Grid container xs={12} justifyContent="center" sx={{ width: "auto" }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                disabled={loading}
                hideNextButton={!hasNext || page === totalPages}
                hidePrevButton={!hasPrev || page === 1}
              />
            </Grid>
          </div>
        )}
      </div>

      {/* Filter drawer */}
      <ActivityLogFilter
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        value={filters}
        onApply={handleApplyFilters}
        resultCount={totalCount ?? 0}
      />
    </div>
  );
}


